import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  LogOut,
  RefreshCw,
  User as UserIcon,
  Building2,
  TrendingUp,
  TrendingDown,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RotateCcw,
  ChevronRight,
  IndianRupee,
} from 'lucide-react';
import DemoResetButton from '@/components/DemoResetButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
const fmtAmount = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString('en-IN')}`;
};
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));
const fmtPct = (n) => (n == null ? '—' : `${(Number(n) * 100).toFixed(1)}%`);

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------
const KpiTile = ({ label, value, sub, tone = 'slate', icon: Icon, onClick, testid }) => {
  const tones = {
    slate: { bar: '#334155', text: '#0f172a' },
    emerald: { bar: '#059669', text: '#065f46' },
    amber: { bar: '#d97706', text: '#78350f' },
    red: { bar: '#dc2626', text: '#7f1d1d' },
    blue: { bar: '#2563eb', text: '#1e3a8a' },
    violet: { bar: '#7c3aed', text: '#4c1d95' },
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={testid}
      className={`group relative text-left bg-white border border-gray-200 rounded-md p-3 w-full transition-all ${onClick ? 'hover:border-slate-400 hover:shadow-sm cursor-pointer' : 'cursor-default'}`}
    >
      <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: tones.bar }} />
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
        {Icon && <Icon className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600" />}
      </div>
      <div className="mt-1.5 text-2xl font-bold tabular-nums" style={{ color: tones.text, fontFamily: 'Space Grotesk, sans-serif' }}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">{sub}</div>}
      {onClick && <ChevronRight className="absolute right-2 bottom-2 h-3 w-3 text-gray-300 group-hover:text-slate-500" />}
    </button>
  );
};

const SectionCard = ({ title, subtitle, children, right }) => (
  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <div>
        <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{title}</h3>
        {subtitle && <div className="text-[11px] text-gray-500">{subtitle}</div>}
      </div>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const TileSkeleton = () => (
  <div className="bg-white border border-gray-200 rounded-md p-3">
    <Skeleton className="h-3 w-16 mb-2" />
    <Skeleton className="h-7 w-24" />
  </div>
);

// ---------------------------------------------------------------------------
// Drilldown modal (shows a read-only inbox-style list)
// ---------------------------------------------------------------------------
const DrilldownModal = ({ open, onClose, section, label }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!open || !section) return;
    setLoading(true);
    axios
      .get(`${API}/dashboard/management/drilldown`, { params: { section, limit: 200 } })
      .then((res) => {
        setRows(res.data.cases || []);
        setTotal(res.data.count || 0);
      })
      .catch(() => toast.error('Failed to load drilldown'))
      .finally(() => setLoading(false));
  }, [open, section]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="mgmt-drilldown-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {label}
            <Badge className="bg-slate-100 text-slate-700 border-none text-xs tabular-nums">{total}</Badge>
          </DialogTitle>
          <DialogDescription className="text-xs">Read-only view. Decisions remain with Credit Officers.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">No cases in this slice.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr className="text-left text-gray-600">
                  <th className="px-3 py-2 font-semibold">Farmer</th>
                  <th className="px-3 py-2 font-semibold">District</th>
                  <th className="px-3 py-2 font-semibold">Relationship</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold text-right">Applied/Requested</th>
                  <th className="px-3 py-2 font-semibold text-right">Sanctioned</th>
                  <th className="px-3 py-2 font-semibold">Age / Decided</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-slate-50" data-testid={`mgmt-drilldown-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{r.farmer_name}</div>
                      {r.aadhaar_last4 && <div className="text-[10px] text-gray-400">····{r.aadhaar_last4}</div>}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{r.residential_district}</td>
                    <td className="px-3 py-2 text-gray-700">
                      {r.journey_type === 'NEW_FARMER' ? 'New' : 'Existing'}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className="text-[10px] bg-slate-100 text-slate-700 border-none">{r.assessment_status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                      {fmtAmount(r.sanctioned_limit_requested)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900 font-semibold">
                      {fmtAmount(r.sanctioned_limit_final)}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{r.age_label || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const ManagementDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [fy, setFy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState({ open: false, section: null, label: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, f] = await Promise.all([
        axios.get(`${API}/dashboard/management/kpis`),
        axios.get(`${API}/dashboard/management/fy-summary`),
      ]);
      setKpis(k.data);
      setFy(f.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access denied');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load management dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const openDrill = (section, label) => setDrill({ open: true, section, label });
  const closeDrill = () => setDrill({ open: false, section: null, label: '' });

  const cases = kpis?.cases_summary || {};
  const amountsNew = kpis?.amounts_summary?.new || {};
  const amountsEx = kpis?.amounts_summary?.existing || {};
  const disposal = kpis?.disposal_summary || {};
  const pendency = kpis?.pendency_summary || {};
  const branchScope = kpis?.branch_scope || 'Bank-wide';
  const todayLabel = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="min-h-screen" style={{ background: '#f8fafc' }}>
      {/* Top bar */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <img src="/saakhsetu-logo.png" alt="SaakhSetu" style={{ height: '30px' }} />
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-none" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Management Dashboard
                </h1>
                <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1.5">
                  <Building2 className="h-2.5 w-2.5" />
                  <span className="font-medium">{branchScope}</span>
                  <span>·</span>
                  <span>IST · {todayLabel}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2 px-2.5 py-1 bg-slate-100 rounded text-[11px]">
                <UserIcon className="h-3 w-3 text-slate-600" />
                <span className="text-slate-700 font-medium">{user?.email}</span>
                <Badge className="text-[9px] border-none bg-violet-100 text-violet-800">{user?.role}</Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={load} className="h-8" data-testid="mgmt-refresh-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
              </Button>
              <DemoResetButton role={user?.role} onDone={load} />
              <Button variant="outline" size="sm" onClick={onLogout} className="h-8" data-testid="mgmt-logout-btn">
                <LogOut className="h-3.5 w-3.5 mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-[1600px] mx-auto px-4 lg:px-6 py-5 space-y-5" data-testid="mgmt-dashboard-root">
        {/* Headline row — top 4 metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {loading ? (
            <>
              <TileSkeleton /><TileSkeleton /><TileSkeleton /><TileSkeleton />
            </>
          ) : (
            <>
              <KpiTile
                label="Total Cases"
                value={fmtNum(cases.total)}
                sub={`New ${fmtNum(cases.new)} · Existing ${fmtNum(cases.existing)}`}
                tone="slate"
                icon={TrendingUp}
                testid="mgmt-kpi-total"
              />
              <KpiTile
                label="Pending Disposal"
                value={fmtNum(cases.pending)}
                sub={`>3d ${fmtNum(pendency.over_3d)} · >7d ${fmtNum(pendency.over_7d)} · >15d ${fmtNum(pendency.over_15d)} (Age-wise Pendency)`}
                tone="amber"
                icon={Clock}
                onClick={() => openDrill('pending', 'Pending Disposal')}
                testid="mgmt-kpi-pending"
              />
              <KpiTile
                label="Disposal Status (30D)"
                value={fmtNum(disposal.total)}
                sub={`Approval rate ${fmtPct(disposal.approval_rate)}`}
                tone="emerald"
                icon={CheckCircle2}
                onClick={() => openDrill('approved', 'Approved (Last 30 days)')}
                testid="mgmt-kpi-disposed"
              />
              <KpiTile
                label="Sanctioned Amount (30D)"
                value={fmtAmount((amountsNew.sanctioned || 0) + (amountsEx.sanctioned || 0))}
                sub={`Applied/Requested ${fmtAmount((amountsNew.requested || 0) + (amountsEx.requested || 0))}`}
                tone="blue"
                icon={IndianRupee}
              />
            </>
          )}
        </div>

        {/* Two-column layout: Cases Summary + Disposal Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <SectionCard title="Cases Summary" subtitle="Live case counts by stage and relationship">
            {loading ? (
              <div className="grid grid-cols-2 gap-3">
                <TileSkeleton /><TileSkeleton /><TileSkeleton /><TileSkeleton />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <KpiTile label="New Farmer" value={fmtNum(cases.new)} tone="violet" onClick={() => openDrill('new', 'New Farmer Cases')} testid="mgmt-kpi-new" />
                <KpiTile label="Existing Farmer" value={fmtNum(cases.existing)} tone="blue" onClick={() => openDrill('existing', 'Existing Farmer Cases')} testid="mgmt-kpi-existing" />
                <KpiTile label="In Draft" value={fmtNum(cases.draft)} tone="slate" onClick={() => openDrill('draft', 'Draft Cases')} testid="mgmt-kpi-draft" />
                <KpiTile label="Scored" value={fmtNum(cases.scored)} tone="emerald" onClick={() => openDrill('scored', 'Scored Cases')} testid="mgmt-kpi-scored" />
              </div>
            )}
          </SectionCard>

          <SectionCard title="Disposal Status (30D)" subtitle="Decisions recorded in the last 30 days">
            {loading ? (
              <div className="grid grid-cols-3 gap-3">
                <TileSkeleton /><TileSkeleton /><TileSkeleton />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <KpiTile label="Approved" value={fmtNum(disposal.APPROVED)} tone="emerald" icon={CheckCircle2} onClick={() => openDrill('approved', 'Approved (Last 30 days)')} testid="mgmt-kpi-approved" />
                  <KpiTile label="Rejected" value={fmtNum(disposal.REJECTED)} tone="red" icon={XCircle} onClick={() => openDrill('rejected', 'Rejected (Last 30 days)')} testid="mgmt-kpi-rejected" />
                  <KpiTile label="Returned" value={fmtNum(disposal.RETURNED)} tone="amber" icon={RotateCcw} onClick={() => openDrill('returned', 'Returned (Last 30 days)')} testid="mgmt-kpi-returned" />
                </div>
                <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Median Time Taken for Review</div>
                    <div className="text-gray-700">
                      New <span className="font-semibold">{kpis?.median_ttr_new_label || 'N/A'}</span>
                      <span className="mx-2 text-gray-300">·</span>
                      Existing <span className="font-semibold">{kpis?.median_ttr_existing_label || 'N/A'}</span>
                    </div>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">
                    {fmtPct(disposal.approval_rate)} approval rate
                  </Badge>
                </div>
              </div>
            )}
          </SectionCard>
        </div>

        {/* Amounts Summary — New vs Existing */}
        <SectionCard
          title="Amounts Summary"
          subtitle="Cumulative across disposed cases in the last 30 days"
        >
          {loading ? (
            <div className="grid grid-cols-2 gap-5">
              <Skeleton className="h-40" />
              <Skeleton className="h-40" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                { label: 'New Farmer', data: amountsNew, tone: 'violet', testid: 'mgmt-amounts-new' },
                { label: 'Existing Farmer', data: amountsEx, tone: 'blue', testid: 'mgmt-amounts-existing' },
              ].map(({ label, data, tone, testid }) => {
                const toneBar = tone === 'violet' ? '#7c3aed' : '#2563eb';
                return (
                  <div key={label} className="border border-gray-200 rounded-md overflow-hidden" data-testid={testid}>
                    <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between" style={{ background: '#fafbfc' }}>
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: toneBar }} />
                        <span className="text-sm font-semibold text-gray-900">{label}</span>
                      </div>
                      <Badge className="bg-white text-gray-700 border border-gray-200 text-[10px] tabular-nums">
                        No. of Cases: {fmtNum(data.cases)}
                      </Badge>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {[
                        ['Applied/Requested Amount', fmtAmount(data.requested)],
                        ['Sanctioned Amount', fmtAmount(data.sanctioned)],
                        ['Cumulative Withdrawal Amount', fmtAmount(data.cumulative_withdrawal)],
                        ['Cumulative Repayment Amount', fmtAmount(data.cumulative_repayment)],
                        ['Outstanding Amount', fmtAmount(data.outstanding)],
                        ['No. of Defaults', fmtNum(data.no_of_defaults)],
                      ].map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between px-3 py-2 text-xs">
                          <span className="text-gray-600">{k}</span>
                          <span className="font-semibold text-gray-900 tabular-nums">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        {/* Pendency Summary */}
        <SectionCard title="Age-wise Pendency" subtitle="Age-wise pendency of cases awaiting disposal">
          {loading ? (
            <div className="grid grid-cols-3 gap-3">
              <TileSkeleton /><TileSkeleton /><TileSkeleton />
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <KpiTile
                label="Over 3 days"
                value={fmtNum(pendency.over_3d)}
                tone="amber"
                icon={Clock}
                onClick={() => openDrill('pending_over_3d', 'Pending over 3 days')}
                testid="mgmt-kpi-over3"
              />
              <KpiTile
                label="Over 7 days"
                value={fmtNum(pendency.over_7d)}
                tone="red"
                icon={AlertTriangle}
                onClick={() => openDrill('pending_over_7d', 'Pending over 7 days')}
                testid="mgmt-kpi-over7"
              />
              <KpiTile
                label="Over 15 days"
                value={fmtNum(pendency.over_15d)}
                tone="red"
                icon={AlertTriangle}
                onClick={() => openDrill('pending_over_15d', 'Pending over 15 days')}
                testid="mgmt-kpi-over15"
              />
            </div>
          )}
        </SectionCard>

        {/* Financial Year Cards */}
        <SectionCard
          title="Financial Year Performance"
          subtitle="Decisions recorded within each FY window (Apr–Mar, IST)"
          right={<Badge className="bg-slate-50 text-slate-700 border border-slate-200 text-[10px]">Scope: {branchScope}</Badge>}
        >
          {loading || !fy ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Skeleton className="h-40" /><Skeleton className="h-40" /><Skeleton className="h-40" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(fy.financial_years || []).map((f) => {
                const total = f.cases?.total || 0;
                const approved = f.cases?.approved || 0;
                const rejected = f.cases?.rejected || 0;
                const returned = f.cases?.returned || 0;
                return (
                  <div
                    key={f.fy_label}
                    className={`relative border rounded-md p-4 ${f.is_current ? 'border-emerald-300 bg-emerald-50/30' : 'border-gray-200 bg-white'}`}
                    data-testid={`mgmt-fy-card-${f.fy_label}`}
                  >
                    {f.is_current && (
                      <Badge className="absolute top-2 right-2 bg-emerald-100 text-emerald-800 border-none text-[9px]">Current</Badge>
                    )}
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">FY {f.fy_label}</div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-gray-900 tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {fmtNum(total)}
                      </span>
                      <span className="text-xs text-gray-500">decisions</span>
                    </div>
                    <div className="mt-3 text-xs space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Approved</span>
                        <span className="font-semibold text-gray-900 tabular-nums">{fmtNum(approved)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 flex items-center gap-1.5"><XCircle className="h-3 w-3 text-red-600" /> Rejected</span>
                        <span className="font-semibold text-gray-900 tabular-nums">{fmtNum(rejected)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 flex items-center gap-1.5"><RotateCcw className="h-3 w-3 text-amber-600" /> Returned</span>
                        <span className="font-semibold text-gray-900 tabular-nums">{fmtNum(returned)}</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Sanctioned</div>
                        <div className="font-semibold text-gray-900 tabular-nums">{fmtAmount(f.sanctioned_total)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Approval rate</div>
                        <div className="font-semibold text-gray-900 tabular-nums">{fmtPct(f.approval_rate)}</div>
                      </div>
                    </div>
                    <div className="mt-2 text-[10px] text-gray-400 flex items-center gap-1">
                      <TrendingDown className="h-2.5 w-2.5" /> Median Time Taken for Review: {f.median_ttr_label}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        <div className="text-[11px] text-gray-400 text-center pt-2">
          Read-only management view · Decision authority remains with Credit Officers · Entitlement Score, Policy Band, and Lender Decision are displayed separately in the Officer workflow.
        </div>
      </main>

      <DrilldownModal open={drill.open} section={drill.section} label={drill.label} onClose={closeDrill} />
    </div>
  );
};

export default ManagementDashboard;
